export default {
  metadata: {
    version: '1.0.0',
    id: 'accordion-1.0.0',
    name: 'JDSAccordion',
    description:
      'An accordion is a list of headers that hide or reveal additional content when expanded.',
    usage:
      'Accordion is used when users only need a few specific pieces of content within a page.'
  },
  hierarchy: {
    container: [
      {
        'accordion-header-container': [
          'prefix-slot',
          {
            'text-container': ['title-jds-text', 'subtitle-jds-text']
          },
          'jds_button'
        ]
      },
      {
        'children-container': [
          {
            'child-content-container': ['content-slot']
          }
        ]
      },
      'jds_divider'
    ]
  },
  base: {
    container: {
      'flex-direction': '{a_0}',
      opacity: '{a_15}',
      width: '{a_1}'
    },
    'accordion-header-container': {
      'flex-direction': '{a_2}',
      'align-items': '{a_3}',
      'justify-content': '{a_4}',
      width: '{a_5}',
      'padding-top': '{a_6}',
      gap: '{a_7}'
    },
    'text-container': {
      'flex-direction': '{a_8}',
      'justify-content': '{a_9}',
      'align-items': '{a_10}',
      gap: '{a_11}',
      flex: 1
    },
    jds_button: {
      icon: 'ic_add',
      kind: 'tertiary',
      size: 'medium',
      state: 'normal'
    },
    'children-container': {
      hidden: true,
      'padding-top': '{a_12}',
      width: '{a_13}',
      'enter-animation-name': 'slideVerticallyDown',
      'exit-animation-name': 'slideVerticallyUp',
      'enter-animation-curve': 'entrance_ease',
      'exit-animation-curve': 'exit_ease',
      'enter-animation-duration': 500,
      'exit-animation-duration': 500,
      overflow: 'hidden',
      transition: {
        property: ['max-height', 'padding-top'],
        duration: '{medium}',
        'timing-function': '{easeExit}'
      }
    },
    'child-content-container': {
      width: '{a_14}'
    },
    jds_divider: {
      orientation: 'horizontal',
      pad: 'xtra-xtra-small',
      padPosition: 'top'
    },
    'title-jds-text': {
      'font-family': '{text_182}',
      color: '{text_20}',
      'font-size': '{text_181}',
      'line-height': '{text_183}',
      'letter-spacing': '{text_184}',
      'font-weight': '{text_185}',
      'text-decoration': '{text_7}',
      'text-transform': '{text_base_text_text-transform}'
    },
    'subtitle-jds-text': {
      'font-family': '{text_147}',
      color: '{text_21}',
      'font-size': '{text_146}',
      'line-height': '{text_148}',
      'letter-spacing': '{text_149}',
      'font-weight': '{text_150}',
      'text-decoration': '{text_7}',
      'text-transform': '{text_base_text_text-transform}'
    }
  },
  variant: {
    disabled: {
      true: {
        container: {
          disabled: true,
          opacity: '{a_17}'
        },
        'accordion-header-container': {
          disabled: true
        },
        jds_button: {
          disabled: true
        }
      }
    },
    expandedPanel: {
      true: {
        'children-container': {
          hidden: false,
          'padding-top': '{a_16}',
          transition: {
            'timing-function': '{easeEntrance}'
          }
        }
      }
    },
    showDivider: {
      false: {
        jds_divider: {
          hidden: true
        }
      }
    }
  },
  combination: [
    {
      plus: {
        true: {
          jds_button: {
            icon: '{a_18}'
          }
        },
        false: {
          jds_button: {
            icon: '{a_19}'
          }
        }
      },
      chevron: {
        true: {
          jds_button: {
            icon: '{a_20}'
          }
        },
        false: {
          jds_button: {
            icon: '{a_21}'
          }
        }
      }
    }
  ],
  combination_config: [['iconType', 'expandedPanel']],
  api: {
    children: {
      'content-slot': {
        name: 'children',
        accepts: ['any'],
        max: 1,
        doc_description:
          'It can be used to display content as children of Accordion.'
      },
      'prefix-slot': {
        name: 'prefix',
        accepts: ['jds_avatar', 'jds_icon'],
        max: 1,
        doc_description:
          'It can be used to display `avatar` or `icon` to the left of text.'
      }
    },
    config: {
      expandedPanel: {
        values: [false, true],
        'doc-description':
          'If false/undefined, only allows one accordionPanel to be expanded by collapsing others on each click.'
      },
      disabled: {
        values: [false, true],
        doc_description: 'It can be used to enable or disable Accordion.'
      },
      showDivider: {
        values: [true, false]
      },
      iconType: {
        values: ['chevron', 'plus'],
        doc_description:
          'The icon to be displayed. The plus icon changes to a minus icon when the corresponding panel is expanded.'
      }
    },
    data: {
      'children-container': {
        'max-height': {
          name: '_childrenHeight',
          type: 'size'
        }
      },
      'title-jds-text': {
        value: {
          name: 'title',
          type: 'string',
          doc_description: 'This displays the title of Accordion.'
        }
      },
      'subtitle-jds-text': {
        value: {
          name: 'subtitle',
          type: 'string',
          doc_description: 'This displays the subtitle of Accordion.'
        }
      }
    },
    events: {
      'accordion-header-container': {
        onClick: 'onClick'
      },
      jds_button: {
        onClick: 'onClick'
      }
    },
    a11y: {
      'accordion-header-container': {
        properties: {
          role: {
            value: 'region'
          }
        }
      },
      jds_button: {
        properties: {
          ariaExpanded: {
            name: '_ariaExpanded',
            prop: 'expandedPanel'
          },
          ariaControls: {
            name: '_ariaControls'
          },
          ariaLabel: {
            name: 'buttonAriaLabel',
            value: 'Toggle accordion'
          }
        }
      }
    }
  }
};
